import * as React from 'react';
import {
  Alert,
  AlertActionCloseButton,
  AlertActionLink,
  AlertGroup,
  Button,
  Card,
  Checkbox,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Dropdown,
  DropdownItem,
  DropdownList,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Pagination,
  Progress,
  SearchInput,
  Select,
  SelectOption,
  Stack,
  StackItem,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { PageContextActions, PageHeaderTitle } from '@app/components/PageContextActions';
import { StatusLabel } from '@app/components/StatusLabel';
import { TruncatedLabel } from '@app/components/TruncatedLabel';
import { CheckCircleIcon, ClockIcon, CodeBranchIcon, EllipsisVIcon, ExclamationCircleIcon, ExclamationTriangleIcon, ExternalLinkAltIcon, EyeIcon, PlusIcon, SpinnerIcon, SyncAltIcon, TrashIcon, UploadIcon, AngleDownIcon, AngleRightIcon, RedoIcon, CaretDownIcon, CloudSecurityIcon, VirtualMachineIcon, ServerGroupIcon, DownloadIcon, InfoCircleIcon, BuilderImageIcon } from '@patternfly/react-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { getImageBuildsBasePath } from '@app/utils/appContext';
import { ImageBuild, mockImageBuilds, mockImageExports, ImageExport } from '../data/imageBuildData';
import { formatDate, formatDateWithRelativeTime } from '../utils/dateFormatter';

// Helper function to format export format labels
const getExportFormatLabel = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return 'VMware Disk';
    case 'qcow2':
      return 'Virtual Disk';
    case 'qcow2-container':
      return 'Disk Container';
    case 'iso':
      return 'Installer Image';
    default:
      return format.toUpperCase();
  }
};

// Helper function to get export format title without acronyms
const getExportFormatTitle = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return '.VMDK';
    case 'qcow2':
      return '.QCOW2';
    case 'qcow2-container':
      return '.QCOW2 (Container)';
    case 'iso':
      return '.ISO';
    default:
      return `.${format.toUpperCase()}`;
  }
};

// Helper function to get export format description
const getExportFormatDescription = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'iso':
      return 'For physical hardware, bare metal, and manual setups.';
    case 'qcow2-container':
      return 'For OpenShift Virtualization and cloud environments.';
    case 'vmdk':
      return 'For vSphere and enterprise VMWare platforms.';
    case 'qcow2':
      return 'For storing and pulling disks from container registries.';
    default:
      return '';
  }
};

// Helper function to get suggested export filename (base + suffix + extension per format)
const getExportFilename = (build: ImageBuild, format: string): string => {
  const base = build.outputImage ? build.outputImage.split(':')[0] : build.name;
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'iso':
      return `${base}-installer.iso`;
    case 'qcow2-container':
      return `${base}-container-disk.qcow2`;
    case 'vmdk':
      return `${base}-vmware.vmdk`;
    case 'qcow2':
      return `${base}-generic-disk.qcow2`;
    default:
      return base;
  }
};

// Helper function to get export format file size
const getExportFormatSize = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return '1 GB';
    case 'qcow2':
      return '1.1 GB';
    case 'iso':
      return '1.5 GB';
    default:
      return '';
  }
};

// Helper function to get export format tooltip content
const getExportFormatTooltipContent = (format: string): React.ReactNode => {
  const formatLower = format.toLowerCase();
  const size = getExportFormatSize(formatLower);
  
  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
        How to use this image (~20 min total)
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Download:</strong> Save the {size} file to your local drive.
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Import:</strong> Open your VM software (e.g., VirtualBox) and select 'Import'.
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Boot:</strong> Power on the VM to complete the automated setup.
      </div>
      <div style={{ marginTop: '8px', fontStyle: 'italic', fontSize: 'var(--pf-t--global--font--size--sm)' }}>
        Note: Download speeds vary based on your network and registry access.
      </div>
    </div>
  );
};

// Helper function to format date to YYYY-MM-DD HH:mm format
const formatDateWithOrdinal = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (error) {
    return dateString;
  }
};

const ImageBuilds: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [searchValue, setSearchValue] = React.useState('');
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState('Name');
  const [isExportFilterOpen, setIsExportFilterOpen] = React.useState(false);
  const [selectedExportFilter, setSelectedExportFilter] = React.useState('Name');
  const [imageBuilds, setImageBuilds] = React.useState<ImageBuild[]>(mockImageBuilds);
  const [openDropdowns, setOpenDropdowns] = React.useState<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());
  const [selectedImageBuilds, setSelectedImageBuilds] = React.useState<Set<string>>(new Set());
  const [showDownloadToast, setShowDownloadToast] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [buildToDelete, setBuildToDelete] = React.useState<ImageBuild | null>(null);
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [buildToCancel, setBuildToCancel] = React.useState<ImageBuild | null>(null);
  const [showRebuildModal, setShowRebuildModal] = React.useState(false);
  const [buildToRebuild, setBuildToRebuild] = React.useState<ImageBuild | null>(null);
  const [rebuildSource, setRebuildSource] = React.useState<'row' | 'card' | null>(null);
  const [showExportErrorToast, setShowExportErrorToast] = React.useState(false);
  // Generate export tasks from builds; Pixel Panda (build-1) and Glitchy Kitty (build-6) use mockImageExports for correct statuses
  const imageExports = React.useMemo(() => {
    const exports: ImageExport[] = [];
    imageBuilds.forEach(build => {
      if (build.id === 'build-1') {
        const build1Exports = mockImageExports.filter(e => e.buildId === 'build-1');
        exports.push(...build1Exports);
        return;
      }
      if (build.id === 'build-6') {
        const build6Exports = mockImageExports.filter(e => e.buildId === 'build-6');
        exports.push(...build6Exports);
        return;
      }
      // Get export formats from build, or use default formats
      const formats = build.exportFormats || ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
      const formatsToUse = formats.slice(0, Math.min(4, Math.max(2, formats.length)));
      
      formatsToUse.forEach((format, index) => {
        const formatLower = format.toLowerCase();
        let formatUpper: 'VMDK' | 'QCOW2' | 'ISO' | 'QCOW2-CONTAINER';
        if (formatLower === 'qcow2-container') {
          formatUpper = 'QCOW2-CONTAINER';
        } else {
          formatUpper = format.toUpperCase() as 'VMDK' | 'QCOW2' | 'ISO';
        }
        let exportStatus: ImageExport['status'] = 'pending';
        if (build.status === 'complete') {
          exportStatus = formatUpper === 'VMDK' ? 'complete' : formatUpper === 'QCOW2' || formatUpper === 'QCOW2-CONTAINER' ? 'exporting' : 'pending';
        } else if (build.status === 'failed') {
          exportStatus = index === 0 ? 'failed' : 'pending';
        } else if (build.status === 'pushing' || build.status === 'building') {
          exportStatus = 'exporting';
        } else {
          exportStatus = 'pending';
        }
        
        exports.push({
          id: `export-${build.id}-${format}-${index}`,
          buildId: build.id,
          buildName: build.buildName || build.name,
          format: formatUpper,
          status: exportStatus,
          created: build.created,
          errorMessage: build.status === 'failed' && index === 0
            ? 'Export failed due to insufficient storage space. Please free up space and retry.'
            : undefined,
          logs: formatUpper === 'VMDK' && build.status === 'complete' ? [
            'Starting export process...',
            'Converting image to VMDK format...',
            'Export completed successfully'
          ] : (formatUpper === 'QCOW2' || formatUpper === 'QCOW2-CONTAINER') && (build.status === 'complete' || build.status === 'pushing' || build.status === 'building') ? [
            'Starting export process...',
            'Converting image to QCOW2 format...',
            'Export in progress...'
          ] : [],
        });
      });
    });
    return exports;
  }, [imageBuilds]);
  
  // Pagination state
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);
  
  // Export tasks pagination state
  const [exportPage, setExportPage] = React.useState(1);
  const [exportPerPage, setExportPerPage] = React.useState(10);

  // Helper function to get the correct base path based on current location
  const getBasePath = () => getImageBuildsBasePath(location.pathname);

  const navigateToCreateImageBuild = (buildData?: ImageBuild) => {
    const basePath = `${getBasePath()}/create`;
    
    if (buildData) {
      navigate(basePath, { state: { retryBuild: buildData } });
    } else {
      navigate(basePath);
    }
  };

  const navigateToImageBuildDetails = (buildId: string, tab?: string) => {
    const basePath = getBasePath();
    const path = tab ? `${basePath}/${buildId}?tab=${tab}` : `${basePath}/${buildId}`;
    navigate(path);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const onFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const onFilterSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setSelectedFilter(value as string);
    setIsFilterOpen(false);
  };

  const onExportFilterToggle = () => {
    setIsExportFilterOpen(!isExportFilterOpen);
  };

  const onExportFilterSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setSelectedExportFilter(value as string);
    setIsExportFilterOpen(false);
  };

  const exportFilterToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onExportFilterToggle}
      isExpanded={isExportFilterOpen}
      style={{ width: '100%', maxWidth: '200px' }}
    >
      {selectedExportFilter}
    </MenuToggle>
  );

  const filterToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onFilterToggle}
      isExpanded={isFilterOpen}
      style={{ width: '100%', maxWidth: '200px' }}
    >
      {selectedFilter}
    </MenuToggle>
  );

  // Filter builds based on search and sort with failed first, then complete
  const filteredBuilds = React.useMemo(() => {
    let builds = imageBuilds;
    
    // Filter by search if search value exists
    if (searchValue) {
      builds = builds.filter(build =>
        (build.buildName || build.name).toLowerCase().includes(searchValue.toLowerCase()) ||
        build.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        build.id.toLowerCase().includes(searchValue.toLowerCase()) ||
        (build.sourceImage || build.inputImage).toLowerCase().includes(searchValue.toLowerCase()) ||
        build.outputImage.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    
    // Sort: queued, pushing, building first (by time), then failed, then complete, then others (all by time, most recent first)
    return [...builds].sort((a, b) => {
      const dateA = new Date(a.created).getTime();
      const dateB = new Date(b.created).getTime();
      
      // Priority order: pushing, building, queued, failed, complete, others
      const getPriority = (status: string): number => {
        if (status === 'pushing') return 1;
        if (status === 'building') return 2;
        if (status === 'queued') return 3;
        if (status === 'failed') return 4;
        if (status === 'complete') return 5;
        return 6;
      };
      
      const priorityA = getPriority(a.status);
      const priorityB = getPriority(b.status);
      
      // If different priorities, sort by priority
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If same priority, sort by time (most recent first)
      return dateB - dateA;
    });
  }, [imageBuilds, searchValue]);

  // Pagination logic
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedBuilds = filteredBuilds.slice(startIndex, endIndex);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => {
    setPage(newPage);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  React.useEffect(() => {
    setPage(1); // Reset to first page when searching
  }, [searchValue]);

  const onActionDropdownToggle = (buildId: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [buildId]: !prev[buildId]
    }));
  };

  const onActionDropdownSelect = (buildId: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [buildId]: false
    }));
  };

  const handleSelectImageBuild = (buildId: string, isSelected: boolean) => {
    setSelectedImageBuilds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(buildId);
      } else {
        newSet.delete(buildId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedImageBuilds(new Set(paginatedBuilds.map(b => b.id)));
    } else {
      setSelectedImageBuilds(new Set());
    }
  };

  const isAllSelected = selectedImageBuilds.size === paginatedBuilds.length && paginatedBuilds.length > 0;
  const isPartiallySelected = selectedImageBuilds.size > 0 && selectedImageBuilds.size < paginatedBuilds.length;

  const toggleRowExpansion = (buildId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(buildId)) {
        newSet.delete(buildId);
      } else {
        newSet.add(buildId);
      }
      return newSet;
    });
  };

  const isRowExpanded = (buildId: string) => expandedRows.has(buildId);

  const getStatusBadge = (status: ImageBuild['status']) => {
    const statusConfig: Record<string, { status: 'success' | 'info' | 'danger' | 'pending' | 'unknown'; label: string; pulse?: boolean }> = {
      complete: { status: 'success', label: 'Complete' },
      pushing: { status: 'info', label: 'Pushing', pulse: true },
      failed: { status: 'danger', label: 'Failed' },
      building: { status: 'info', label: 'Building', pulse: true },
      queued: { status: 'pending', label: 'Queued' },
      unknown: { status: 'unknown', label: 'Unknown' },
    };
    const config = statusConfig[status] || statusConfig.unknown;
    return (
      <StatusLabel status={config.status} pulse={config.pulse}>
        {config.label}
      </StatusLabel>
    );
  };

  const getExportStatusBadge = (status: ImageExport['status']) => {
    const statusConfig = {
      complete: { status: 'success' as const, label: 'Complete' },
      failed: { status: 'danger' as const, label: 'Failed' },
      pending: { status: 'pending' as const, label: 'Pending' },
      exporting: { status: 'info' as const, label: 'In progress', pulse: true },
    };
    const config = statusConfig[status];
    return (
      <StatusLabel status={config.status} pulse={'pulse' in config && config.pulse}>
        {config.label}
      </StatusLabel>
    );
  };

  return (
    <>
      {/* Page Header */}
      <PageSection hasBodyWrapper={false} className="acm-page__header-section">
        <div className="acm-page__header-inner">
          <Flex 
            justifyContent={{ default: 'justifyContentSpaceBetween' }}
            alignItems={{ default: 'alignItemsCenter' }}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
          >
            <PageHeaderTitle
              title="Image builds"
              description="Create and manage system images for your edge devices and export them in various formats."
            />
            <PageContextActions placement="toolbar" />
          </Flex>
        </div>
      </PageSection>

      {/* Main Content — white panel: filters, search, CTA, table, pagination */}
      <PageSection hasBodyWrapper={false} className="acm-page__content-section">
        <div className="acm-page__panel">
          <div className="acm-page__panel-toolbar">
            <Toolbar>
              <ToolbarContent>
                {selectedImageBuilds.size > 0 ? (
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                    <ToolbarItem>
                      <Content component="p" style={{ margin: 0, marginRight: 'var(--pf-t--global--spacer--md)' }}>
                        {selectedImageBuilds.size} selected
                      </Content>
                    </ToolbarItem>
                    <ToolbarItem>
                      <Button variant="danger" onClick={() => setSelectedImageBuilds(new Set())}>
                        Delete
                      </Button>
                    </ToolbarItem>
                  </ToolbarGroup>
                ) : (
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                <ToolbarItem>
                  <Select
                    toggle={filterToggle}
                    onSelect={onFilterSelect}
                    selected={selectedFilter}
                    isOpen={isFilterOpen}
                    onOpenChange={setIsFilterOpen}
                  >
                    <SelectOption value="Name">Name</SelectOption>
                    <SelectOption value="Tag">Tag</SelectOption>
                    <SelectOption value="Status">Status</SelectOption>
                  </Select>
                </ToolbarItem>
                <ToolbarItem>
                  <SearchInput
                    className="app-search-input"
                    placeholder="Search"
                    value={searchValue}
                    onChange={(_, value) => handleSearchChange(value)}
                    onClear={() => handleSearchChange('')}
                  />
                </ToolbarItem>
                <ToolbarItem>
                  <Button
                    variant="primary"
                    onClick={() => navigateToCreateImageBuild()}
                  >
                    Build new image
                  </Button>
                </ToolbarItem>
                    </ToolbarGroup>
                )}
              </ToolbarContent>
            </Toolbar>
          </div>

            <BuildImagesTabContent
              filteredBuilds={filteredBuilds}
              paginatedBuilds={paginatedBuilds}
              openDropdowns={openDropdowns}
              expandedRows={expandedRows}
              selectedImageBuilds={selectedImageBuilds}
              page={page}
              perPage={perPage}
              onActionDropdownToggle={onActionDropdownToggle}
              onActionDropdownSelect={onActionDropdownSelect}
              setOpenDropdowns={setOpenDropdowns}
              toggleRowExpansion={toggleRowExpansion}
              isRowExpanded={isRowExpanded}
              handleSelectImageBuild={handleSelectImageBuild}
              handleSelectAll={handleSelectAll}
              isAllSelected={isAllSelected}
              isPartiallySelected={isPartiallySelected}
              getStatusBadge={getStatusBadge}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
              formatDate={formatDate}
              navigate={navigate}
              navigateToImageBuildDetails={navigateToImageBuildDetails}
              navigateToCreateImageBuild={navigateToCreateImageBuild}
              imageExports={imageExports}
              getExportStatusBadge={getExportStatusBadge}
              getExportFormatLabel={getExportFormatLabel}
              getExportFormatTitle={getExportFormatTitle}
              getExportFormatDescription={getExportFormatDescription}
              location={location}
              setShowDownloadToast={setShowDownloadToast}
              setShowDeleteModal={setShowDeleteModal}
              setBuildToDelete={setBuildToDelete}
              setShowCancelModal={setShowCancelModal}
              setBuildToCancel={setBuildToCancel}
              setShowRebuildModal={setShowRebuildModal}
              setBuildToRebuild={setBuildToRebuild}
              setRebuildSource={setRebuildSource}
              setShowExportErrorToast={setShowExportErrorToast}
            />
        </div>
      </PageSection>
      
      {/* Download Started Modal - VMDK only */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showDownloadToast}
        onClose={() => setShowDownloadToast(false)}
      >
        <ModalHeader title="Download Started!" titleIconVariant="success" />
        <ModalBody>
          <div>
            While your 1GB image downloads, here's how to start:
            <div style={{ marginTop: '16px' }}>
              <div style={{ marginBottom: '8px' }}>• Import the file into your VM software.</div>
              <div style={{ marginBottom: '8px' }}>• Power on the VM.</div>
              <div>• Wait about 10 mins for the first-time setup to finish.</div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => setShowDownloadToast(false)}>
            Got it
          </Button>
        </ModalFooter>
      </Modal>

      {/* Export Error Toast Alert */}
      <AlertGroup isToast>
        {showExportErrorToast && (
          <Alert
            variant="danger"
            title="We couldn't export your file"
            actionClose={
              <AlertActionCloseButton
                onClose={() => setShowExportErrorToast(false)}
              />
            }
          >
            Something went wrong on our end. Please check your internet and try again.
          </Alert>
        )}
      </AlertGroup>

      {/* Delete Image Build Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setBuildToDelete(null);
        }}
      >
        <ModalHeader title="Delete this build?" />
        <ModalBody>
          <div>
            <div style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
              This will remove the record of this build and its history.
            </div>
            <div style={{ color: 'var(--pf-t--global--text--color--subtle)', fontSize: 'var(--pf-t--global--font--size--sm)' }}>
              The actual image files in your storage will not be deleted.
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            key="delete"
            variant="danger"
            onClick={() => {
              if (buildToDelete) {
                // TODO: Implement delete logic
                console.log(`Deleting build: ${buildToDelete.id}`);
                // Remove build from state
                setImageBuilds(prev => prev.filter(b => b.id !== buildToDelete.id));
                setShowDeleteModal(false);
                setBuildToDelete(null);
              }
            }}
          >
            Delete
          </Button>
          <Button
            key="cancel"
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setBuildToDelete(null);
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Cancel and discard build modal (for pushing, building, queued) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setBuildToCancel(null);
        }}
      >
        <ModalHeader title="Cancel and discard build?" />
        <ModalBody>
          This will immediately stop the current process. All progress will be lost, and the associated data will be permanently deleted.
        </ModalBody>
        <ModalFooter>
          <Button
            key="confirm-cancel"
            variant="danger"
            onClick={() => {
              if (buildToCancel) {
                setImageBuilds(prev => prev.filter(b => b.id !== buildToCancel.id));
                setShowCancelModal(false);
                setBuildToCancel(null);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            key="close"
            variant="secondary"
            onClick={() => {
              setShowCancelModal(false);
              setBuildToCancel(null);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Rebuild image confirmation (ellipsis = row, card = Export images tab message) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showRebuildModal}
        onClose={() => {
          setShowRebuildModal(false);
          setBuildToRebuild(null);
          setRebuildSource(null);
        }}
      >
        <ModalHeader title="Rebuild image?" />
        <ModalBody>
          {rebuildSource === 'row'
            ? 'This image will be rebuilt using the current parameters. To make changes, edit the image configuration.'
            : 'Rebuilding updates the image currently displayed in the console. Previous versions remain accessible on the Export images tab of the Details page.'}
        </ModalBody>
        <ModalFooter>
          <Button
            key="rebuild"
            variant="primary"
            onClick={() => {
              if (buildToRebuild) {
                navigateToCreateImageBuild(buildToRebuild);
                setShowRebuildModal(false);
                setBuildToRebuild(null);
                setRebuildSource(null);
              }
            }}
          >
            Rebuild
          </Button>
          <Button
            key="cancel"
            variant="secondary"
            onClick={() => {
              setShowRebuildModal(false);
              setBuildToRebuild(null);
              setRebuildSource(null);
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

// Build Images Tab Content Component
const BuildImagesTabContent: React.FunctionComponent<{
  filteredBuilds: ImageBuild[];
  paginatedBuilds: ImageBuild[];
  openDropdowns: Record<string, boolean>;
  expandedRows: Set<string>;
  selectedImageBuilds: Set<string>;
  page: number;
  perPage: number;
  onActionDropdownToggle: (buildId: string) => void;
  onActionDropdownSelect: (buildId: string) => void;
  setOpenDropdowns: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  toggleRowExpansion: (buildId: string) => void;
  isRowExpanded: (buildId: string) => boolean;
  handleSelectImageBuild: (buildId: string, isSelected: boolean) => void;
  handleSelectAll: (isSelected: boolean) => void;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  getStatusBadge: (status: ImageBuild['status']) => React.ReactElement;
  onSetPage: (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => void;
  onPerPageSelect: (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => void;
  formatDate: (dateString: string) => string;
  navigate: (path: string) => void;
  navigateToImageBuildDetails: (buildId: string, tab?: string) => void;
  navigateToCreateImageBuild: (buildData?: ImageBuild) => void;
  imageExports: ImageExport[];
  getExportStatusBadge: (status: ImageExport['status']) => React.ReactElement;
  getExportFormatLabel: (format: string) => string;
  getExportFormatTitle: (format: string) => string;
  getExportFormatDescription: (format: string) => string;
  location: ReturnType<typeof useLocation>;
  setShowDownloadToast: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setBuildToDelete: React.Dispatch<React.SetStateAction<ImageBuild | null>>;
  setShowCancelModal: React.Dispatch<React.SetStateAction<boolean>>;
  setBuildToCancel: React.Dispatch<React.SetStateAction<ImageBuild | null>>;
  setShowRebuildModal: React.Dispatch<React.SetStateAction<boolean>>;
  setBuildToRebuild: React.Dispatch<React.SetStateAction<ImageBuild | null>>;
  setRebuildSource: React.Dispatch<React.SetStateAction<'row' | 'card' | null>>;
  setShowExportErrorToast: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  filteredBuilds,
  paginatedBuilds,
  openDropdowns,
  expandedRows,
  selectedImageBuilds,
  page,
  perPage,
  onActionDropdownToggle,
  onActionDropdownSelect,
  setOpenDropdowns,
  toggleRowExpansion,
  isRowExpanded,
  handleSelectImageBuild,
  handleSelectAll,
  isAllSelected,
  isPartiallySelected,
  getStatusBadge,
  onSetPage,
  onPerPageSelect,
  formatDate,
  navigate,
  navigateToImageBuildDetails,
  navigateToCreateImageBuild,
  imageExports,
  getExportStatusBadge,
  getExportFormatLabel,
  getExportFormatTitle,
  getExportFormatDescription,
  location,
  setShowDownloadToast,
  setShowDeleteModal,
  setBuildToDelete,
  setShowCancelModal,
  setBuildToCancel,
  setShowRebuildModal,
  setBuildToRebuild,
  setRebuildSource,
  setShowExportErrorToast,
}) => {
  const [openExportDropdowns, setOpenExportDropdowns] = React.useState<Record<string, boolean>>({});
  const [openActionsDropdown, setOpenActionsDropdown] = React.useState<Record<string, boolean>>({});
  
  return (
    <Stack hasGutter>
          {filteredBuilds.length === 0 ? (
            <StackItem isFilled>
              <EmptyState variant="lg">
                <Icon size="xl">
                  <PlusIcon />
                </Icon>
                <Title headingLevel="h4" size="lg">
                  There are no images in your environment.
                </Title>
                <EmptyStateBody>
                  Image builder is a tool for creating customized, deployment-ready system images, installation disks, virtual machines, and cloud vendor-specific images.
                </EmptyStateBody>
                <EmptyStateFooter>
                  <Button variant="primary" icon={<PlusIcon />}>
                    Build a new image
                  </Button>
                  <EmptyStateActions>
                    <Button variant="link" icon={<ExternalLinkAltIcon />} iconPosition="right">
                      Learn more about Image builder
                    </Button>
                  </EmptyStateActions>
                </EmptyStateFooter>
              </EmptyState>
            </StackItem>
          ) : (
            <>
              <StackItem isFilled>
                {/* Image Builds Table */}
                <div className="image-builds-table app-data-table app-data-table--visible-overflow" style={{ position: 'relative' }}>
                  {/* Table Header */}
                  <div className="image-builds-table__header app-data-table__header app-data-table__grid app-data-table__grid--image-builds">
                    <div style={{ display: 'flex', justifyContent: 'center', marginRight: '-8px' }}>
                      <Checkbox
                        id="select-all-builds"
                        isChecked={isAllSelected}
                        onChange={(_, checked) => handleSelectAll(checked)}
                        aria-label="Select all builds"
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', marginLeft: '-8px' }}></div>
                    <div>Name</div>
                    <div>Input image</div>
                    <div>Output image</div>
                    <div>Status</div>
                    <div>Export images</div>
                    <div>Date</div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}></div>
                  </div>

                  {/* Table Rows */}
                  {paginatedBuilds.map((build, index) => {
                    const isExpanded = isRowExpanded(build.id);
                    return (
                      <React.Fragment key={build.id}>
                        <div
                          className={`image-builds-table__row app-data-table__grid app-data-table__grid--image-builds${isExpanded ? ' image-builds-table__row--expanded' : ''}`}
                          style={{
                            borderBottom: isExpanded || index < paginatedBuilds.length - 1
                              ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)' 
                              : 'none',
                            backgroundColor: isExpanded ? 'var(--pf-t--global--color--nonstatus--gray--50, #fafafa)' : 'transparent',
                          }}
                        >
                          <div className="app-data-table__control app-data-table__control--select" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '-8px' }}>
                            <Checkbox
                              id={`select-build-${build.id}`}
                              isChecked={selectedImageBuilds.has(build.id)}
                              onChange={(_, checked) => handleSelectImageBuild(build.id, checked)}
                              aria-label={`Select ${build.name}`}
                            />
                          </div>
                          <div className="app-data-table__control app-data-table__control--expand" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '-8px' }}>
                            <Button
                              variant="plain"
                              onClick={() => toggleRowExpansion(build.id)}
                              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                              aria-expanded={isExpanded}
                              style={{ padding: '4px' }}
                            >
                              {isExpanded ? <AngleDownIcon /> : <AngleRightIcon />}
                            </Button>
                          </div>
                          <div data-label="Name">
                            <Button
                              variant="link"
                              onClick={() => navigateToImageBuildDetails(build.id)}
                              style={{
                                padding: 0,
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                textAlign: 'left',
                              }}
                            >
                              <TruncatedLabel content={build.buildName || build.name} />
                            </Button>
                          </div>
                          <div data-label="Input image">
                            <TruncatedLabel content={build.sourceImage || build.inputImage} />
                          </div>
                          <div data-label="Output image">
                            <TruncatedLabel content={build.outputImage} />
                          </div>
                          <div data-label="Status" style={{ display: 'flex', alignItems: 'center', gap: 'var(--pf-t--global--spacer--sm)', flexWrap: 'nowrap' }}>
                            {getStatusBadge(build.status)}
                          </div>
                          <div data-label="Export images" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'nowrap' }}>
                            {(() => {
                              const exportCount = imageExports.filter(exp => exp.buildId === build.id).length;
                              const isInactiveStatus = build.status === 'failed' || build.status === 'building' || build.status === 'queued' || build.status === 'pushing';
                              const displayCount = isInactiveStatus ? 0 : exportCount;
                              
                              return (
                                <span style={{ flexShrink: 0 }}>{displayCount}</span>
                              );
                            })()}
                          </div>
                          <div data-label="Date">
                            <TruncatedLabel content={formatDateWithRelativeTime(build.created)} />
                          </div>
                          <div className="app-data-table__control app-data-table__control--actions" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Dropdown
                              isOpen={openDropdowns[build.id] || false}
                              onSelect={(event, value) => {
                                console.log('Dropdown selected value:', value);
                                onActionDropdownSelect(build.id);
                                
                                // Close dropdown
                                setOpenDropdowns((prev) => ({
                                  ...prev,
                                  [build.id]: false
                                }));
                                
                                if (value === 'view') {
                                  navigateToImageBuildDetails(build.id);
                                } else if (value === 'retry') {
                                  navigateToCreateImageBuild(build);
                                } else if (value === 'edit') {
                                  // Rebuild from ellipsis: show modal with row-specific copy
                                  setBuildToRebuild(build);
                                  setRebuildSource('row');
                                  setShowRebuildModal(true);
                                }
                                // Delete is handled by onClick handler on DropdownItem
                              }}
                              onOpenChange={(isOpen: boolean) => {
                                setOpenDropdowns((prev) => ({
                                  ...prev,
                                  [build.id]: isOpen
                                }));
                              }}
                              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                <Button
                                  ref={toggleRef as React.Ref<HTMLButtonElement>}
                                  variant="plain"
                                  onClick={() => onActionDropdownToggle(build.id)}
                                  aria-expanded={openDropdowns[build.id] || false}
                                  aria-label={`Actions for ${build.name}`}
                                  aria-haspopup="menu"
                                  icon={<EllipsisVIcon />}
                                />
                              )}
                              popperProps={{
                                appendTo: () => document.body,
                                enableFlip: true,
                                position: 'end',
                                minWidth: '150px',
                                maxWidth: '200px',
                              }}
                            >
                              <DropdownList>
                                <DropdownItem
                                  key="view"
                                  value="view"
                                >
                                  View
                                </DropdownItem>
                                {build.status === 'failed' && (
                                  <DropdownItem
                                    key="retry"
                                    value="retry"
                                  >
                                    Retry
                                  </DropdownItem>
                                )}
                                {build.status === 'complete' && (
                                  <DropdownItem
                                    key="edit"
                                    value="edit"
                                    onClick={() => onActionDropdownSelect(build.id)}
                                  >
                                    Rebuild
                                  </DropdownItem>
                                )}
                                <DropdownItem
                                  key="delete"
                                  value="delete"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onActionDropdownSelect(build.id);
                                    setOpenDropdowns((prev) => ({
                                      ...prev,
                                      [build.id]: false
                                    }));
                                    if (['pushing', 'building', 'queued'].includes(build.status)) {
                                      setBuildToCancel(build);
                                      setShowCancelModal(true);
                                    } else {
                                      setBuildToDelete(build);
                                      setShowDeleteModal(true);
                                    }
                                  }}
                                >
                                  {['pushing', 'building', 'queued'].includes(build.status) ? 'Cancel' : 'Delete'}
                                </DropdownItem>
                              </DropdownList>
                            </Dropdown>
                          </div>
                        </div>
                    {/* Expandable Row Content */}
                    {isExpanded && (
                      <div
                        className="image-builds-table__expandable-content"
                        style={{
                          gridColumn: '1 / -1',
                          padding: 'var(--pf-t--global--spacer--lg)',
                          backgroundColor: 'var(--pf-t--global--color--nonstatus--gray--50, #fafafa)',
                          borderBottom: index < paginatedBuilds.length - 1
                            ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)'
                            : 'none',
                        }}
                      >
                        <Stack hasGutter>
                          <StackItem>
                            <Stack hasGutter>
                              <StackItem>
                                <Title headingLevel="h3" size="md" style={{ marginBottom: 0 }}>
                                  Build information
                                </Title>
                              </StackItem>
                              {build.status === 'failed' && (
                                <StackItem>
                                  <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                                    <FlexItem>
                                      <Icon status="danger">
                                        <ExclamationCircleIcon />
                                      </Icon>
                                    </FlexItem>
                                    <FlexItem>
                                      <Content component="p" style={{ margin: 0, fontSize: 'var(--pf-t--global--font--size--sm)', lineHeight: 'var(--pf-t--global--line-height--body)' }}>
                                        Build failed. Please retry again.
                                      </Content>
                                    </FlexItem>
                                    <FlexItem>
                                      <Button variant="link" onClick={() => navigateToCreateImageBuild(build)} style={{ padding: 0, fontSize: 'var(--pf-t--global--font--size--sm)' }}>
                                        Retry
                                      </Button>
                                    </FlexItem>
                                  </Flex>
                                </StackItem>
                              )}
                              <StackItem>
                                <Button
                                  variant="link"
                                  onClick={() => navigateToImageBuildDetails(build.id)}
                                  style={{
                                    fontSize: 'var(--pf-t--global--font--size--sm)',
                                    padding: 0,
                                  }}
                                >
                                  View more
                                </Button>
                              </StackItem>
                            </Stack>
                          </StackItem>
                          <StackItem>
                            {(() => {
                              const buildExports = imageExports.filter(exp => exp.buildId === build.id);
                              
                              // For failed builds, show all 4 export format cards in order: ISO, QCOW2 (Container), VMDK, QCOW2
                              if (build.status === 'failed') {
                                // Define the order: ISO, QCOW2 (Container), VMDK, QCOW2
                                const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                                
                                // Create cards for all 4 formats in the specified order
                                const allFormatCards = formatOrder.map(format => {
                                  const existingExport = buildExports.find(exp => exp.format.toLowerCase() === format);
                                  if (existingExport) {
                                    return existingExport;
                                  } else {
                                    // Create placeholder export for missing format - for failed builds all show failed
                                    let formatUpper: 'VMDK' | 'QCOW2' | 'ISO' | 'QCOW2-CONTAINER';
                                    if (format === 'qcow2-container') formatUpper = 'QCOW2-CONTAINER';
                                    else formatUpper = format.toUpperCase() as 'VMDK' | 'QCOW2' | 'ISO';
                                    const placeholderStatus: 'failed' | 'pending' = 'failed';
                                    return {
                                      id: `export-${build.id}-${format}-placeholder`,
                                    buildId: build.id,
                                    buildName: build.buildName || build.name,
                                    format: formatUpper,
                                      status: placeholderStatus,
                                    created: build.created,
                                    };
                                  }
                                });
                                
                                const formatIconMap: Record<string, React.ReactElement> = {
                                  'vmdk': <VirtualMachineIcon />,
                                  'qcow2': <CloudSecurityIcon />,
                                  'qcow2-container': <BuilderImageIcon />,
                                  'iso': <ServerGroupIcon />
                                };
                                
                                return (
                                  <Stack hasGutter>
                                    <StackItem>
                                      <Flex gap={{ default: 'gapMd' }} alignItems={{ default: 'alignItemsFlexStart' }} wrap="wrap">
                                        {/* All 3 export format cards in order: QCOW2, ISO, VMDK */}
                                        {allFormatCards.map((exp) => {
                                          const isExportFailed = exp.status === 'failed';
                                          const isBuildFailed = build.status === 'failed';
                                          // For failed builds (e.g. Cosmic Noodle), all 4 cards show Failed
                                          const showFailedBadge = isExportFailed || isBuildFailed;
                                          return (
                                            <FlexItem key={exp.id}>
                                          <Card
                                            style={{
                                              boxSizing: 'border-box',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-start',
                                              padding: '16px',
                                              position: 'relative',
                                              width: '235px',
                                              height: 'auto',
                                              minHeight: '196px',
                                              background: 'var(--pf-t--global--background--color--primary--default)',
                                              border: '1px solid var(--pf-t--global--border--color--default)',
                                              borderRadius: '16px',
                                            }}
                                          >
                                                {/* Failed Status Badge in top right - show if export failed or if build failed for QCOW2/ISO */}
                                                {showFailedBadge && (
                                            <div 
                                              style={{
                                                position: 'absolute',
                                                right: '16px',
                                                top: '16px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                zIndex: 1
                                              }}
                                            >
                                              <StatusLabel status="danger">
                                                Failed
                                              </StatusLabel>
                                            </div>
                                                )}
                                            
                                            {/* Card Content - Icon, Title, Description */}
                                            <div style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-start',
                                              padding: '0px',
                                              gap: '16px',
                                              width: '203px',
                                              flex: '1 1 auto',
                                              order: 0
                                            }}>
                                              {/* Icon */}
                                              <div style={{
                                                width: '40px',
                                                height: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flex: 'none',
                                                order: 0,
                                                flexGrow: 0
                                              }}>
                                                <Icon size="xl">
                                                      {formatIconMap[exp.format.toLowerCase()] || <CloudSecurityIcon />}
                                                </Icon>
                                              </div>
                                              
                                              {/* Title + Description */}
                                              <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                padding: '0px',
                                                gap: '4px',
                                                width: '203px',
                                                flex: 'none',
                                                order: 1,
                                                alignSelf: 'stretch',
                                                flexGrow: 0
                                              }}>
                                                {/* Title + info icon (hover shows format filename) */}
                                                <div style={{
                                                  width: '203px',
                                                  height: '24px',
                                                  fontFamily: 'var(--pf-t--global--font--family--heading)',
                                                  fontStyle: 'normal',
                                                      fontWeight: 'var(--pf-t--global--font--weight--heading--md)',
                                                      fontSize: 'var(--pf-t--global--font--size--md)',
                                                      lineHeight: 'var(--pf-t--global--line-height--heading--md)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 'var(--pf-t--global--spacer--xs)',
                                                      color: 'var(--pf-t--global--text--color--default)',
                                                  flex: 'none',
                                                  order: 0,
                                                  alignSelf: 'stretch',
                                                  flexGrow: 0
                                                }}>
                                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getExportFormatTitle(exp.format.toLowerCase())}</span>
                                                  <Tooltip content={getExportFilename(build, exp.format)}>
                                                    <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon size="sm"><InfoCircleIcon /></Icon></span>
                                                  </Tooltip>
                                                </div>
                                                
                                                {/* Description */}
                                                <div style={{
                                                  width: '203px',
                                                  fontFamily: 'var(--pf-t--global--font--family--body)',
                                                  fontStyle: 'normal',
                                                      fontWeight: 'var(--pf-t--global--font--weight--body)',
                                                      fontSize: 'var(--pf-t--global--font--size--sm)',
                                                      lineHeight: 'var(--pf-t--global--line-height--body)',
                                                  display: 'flex',
                                                  alignItems: 'flex-start',
                                                      color: 'var(--pf-t--global--text--color--subtle)',
                                                  flex: 'none',
                                                  order: 1,
                                                  alignSelf: 'stretch',
                                                  flexGrow: 0
                                                }}>
                                                      {getExportFormatDescription(exp.format.toLowerCase())}
                                                </div>
                                              </div>
                                              
                                                  {/* If build is failed, only show View logs button */}
                                                  {isBuildFailed && (
                                              <div style={{
                                                width: '100%',
                                                flex: 'none',
                                                order: 2,
                                                alignSelf: 'stretch',
                                                flexGrow: 0,
                                                display: 'flex',
                                                gap: 'var(--pf-t--global--spacer--sm)',
                                                alignItems: 'center'
                                              }}>
                                                <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  onClick={() => navigateToImageBuildDetails(build.id, 'logs')}
                                                >
                                                  View logs
                                                </Button>
                                              </div>
                                                  )}
                                            
                                                  {/* If build is not failed, show Export image button */}
                                                  {!isBuildFailed && (
                                            <div style={{
                                              width: '100%',
                                              flex: 'none',
                                                      order: 2,
                                              alignSelf: 'stretch',
                                                      flexGrow: 0,
                                                      display: 'flex',
                                                      gap: 'var(--pf-t--global--spacer--sm)',
                                                      alignItems: 'center'
                                              }}>
                                                      <Button
                                                        variant="secondary"
                                                        size="sm"
                                                  onClick={() => {
                                                          navigate(`${getImageBuildsBasePath(location.pathname)}/create-export`);
                                                        }}
                                                      >
                                                        Export image
                                                      </Button>
                                                  </div>
                                                  )}
                                                </div>
                                                
                                                {/* Footer - Created date */}
                                                <div style={{
                                                  width: '100%',
                                                  paddingTop: 'var(--pf-t--global--spacer--md)',
                                                  marginTop: 'auto',
                                                  flex: 'none',
                                                  order: 3,
                                                  alignSelf: 'stretch',
                                                  flexGrow: 0
                                                }}>
                                                  <div style={{
                                                    fontFamily: 'var(--pf-t--global--font--family--body)',
                                                    fontStyle: 'normal',
                                                    fontWeight: 'var(--pf-t--global--font--weight--body)',
                                                    fontSize: 'var(--pf-t--global--font--size--sm)',
                                                    lineHeight: 'var(--pf-t--global--line-height--body)',
                                                    color: 'var(--pf-t--global--text--color--subtle)'
                                                  }}>
                                                    Date: {formatDateWithRelativeTime(exp.created)}
                                                  </div>
                                                </div>
                                              </Card>
                                            </FlexItem>
                                          );
                                        })}
                                        </Flex>
                                      </StackItem>
                                  </Stack>
                                );
                              }
                              
                              // For complete builds, table row shows only: last failed ISO (with Retry), .QCOW2 (Container), .VMDK, .QCOW2
                              if (build.status === 'complete') {
                                const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                                const norm = (f: string) => f.toLowerCase().replace(/_/g, '-');
                                const allExports = buildExports
                                  .filter(exp => exp.buildId === build.id)
                                  .sort((a, b) => {
                                    const aIndex = formatOrder.indexOf(norm(a.format));
                                    const bIndex = formatOrder.indexOf(norm(b.format));
                                    if (aIndex === -1) return 1;
                                    if (bIndex === -1) return -1;
                                    return aIndex - bIndex;
                                  });
                                
                                if (allExports.length === 0) return null;
                                
                                const failedIsoExports = allExports
                                  .filter(e => e.status === 'failed' && norm(e.format) === 'iso')
                                  .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
                                const newestFailedIsoExport = failedIsoExports.length > 0 ? failedIsoExports[0] : null;
                                const lastFailedIsoId = newestFailedIsoExport?.id ?? null;
                                const isoExport = allExports.find(e => norm(e.format) === 'iso');
                                const qcow2Container = allExports.find(e => norm(e.format) === 'qcow2-container');
                                const vmdk = allExports.find(e => norm(e.format) === 'vmdk');
                                const qcow2 = allExports.find(e => norm(e.format) === 'qcow2');
                                const cardsToShow = [newestFailedIsoExport ?? isoExport, qcow2Container, vmdk, qcow2].filter(Boolean) as ImageExport[];
                                
                                const formatIconMap: Record<string, React.ReactElement> = {
                                  'vmdk': <VirtualMachineIcon />,
                                  'qcow2': <CloudSecurityIcon />,
                                  'qcow2-container': <BuilderImageIcon />,
                                  'iso': <ServerGroupIcon />
                                };
                                
                                return (
                                  <Flex gap={{ default: 'gapMd' as const }} wrap="wrap">
                                    {cardsToShow.map((exp) => (
                                      <FlexItem key={exp.id}>
                                        <Card
                                          style={{
                                            boxSizing: 'border-box',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            padding: '16px',
                                            position: 'relative',
                                            width: '235px',
                                            height: 'auto',
                                            minHeight: '196px',
                                            background: 'var(--pf-t--global--background--color--primary--default)',
                                            border: '1px solid var(--pf-t--global--border--color--default)',
                                            borderRadius: '16px',
                                          }}
                                        >
                                          {/* Status Badge in top right */}
                                          <div 
                                            style={{
                                              position: 'absolute',
                                              right: '16px',
                                              top: '16px',
                                              display: 'flex',
                                              justifyContent: 'center',
                                              alignItems: 'center',
                                              zIndex: 1
                                            }}
                                          >
                                            {getExportStatusBadge(exp.status)}
                                          </div>
                                          
                                          {/* Card Content - Icon, Title, Description */}
                                          <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            padding: '0px',
                                            gap: '16px',
                                            width: '203px',
                                            flex: '1 1 auto',
                                            order: 0
                                          }}>
                                            {/* Icon */}
                                            <div style={{
                                              width: '40px',
                                              height: '40px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              flex: 'none',
                                              order: 0,
                                              flexGrow: 0
                                            }}>
                                              <Icon size="xl">
                                                {formatIconMap[exp.format.toLowerCase()] || <CloudSecurityIcon />}
                                              </Icon>
                                            </div>
                                            
                                            {/* Title + Description */}
                                            <div style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-start',
                                              padding: '0px',
                                              gap: '4px',
                                              width: '203px',
                                              flex: 'none',
                                              order: 1,
                                              alignSelf: 'stretch',
                                              flexGrow: 0
                                            }}>
                                              {/* Title + info icon (hover shows format filename) */}
                                              <div style={{
                                                width: '203px',
                                                height: '24px',
                                                fontFamily: 'var(--pf-t--global--font--family--heading)',
                                                fontStyle: 'normal',
                                                fontWeight: 'var(--pf-t--global--font--weight--heading--md)',
                                                fontSize: 'var(--pf-t--global--font--size--md)',
                                                lineHeight: 'var(--pf-t--global--line-height--heading--md)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--pf-t--global--spacer--xs)',
                                                color: 'var(--pf-t--global--text--color--default)',
                                                flex: 'none',
                                                order: 0,
                                                alignSelf: 'stretch',
                                                flexGrow: 0
                                              }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getExportFormatTitle(exp.format.toLowerCase())}</span>
                                                <Tooltip content={getExportFilename(build, exp.format)}>
                                                  <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon size="sm"><InfoCircleIcon /></Icon></span>
                                                </Tooltip>
                                              </div>
                                              
                                              {/* Description */}
                                              <div style={{
                                                width: '203px',
                                                fontFamily: 'var(--pf-t--global--font--family--body)',
                                                fontStyle: 'normal',
                                                  fontWeight: 'var(--pf-t--global--font--weight--body)',
                                                  fontSize: 'var(--pf-t--global--font--size--sm)',
                                                  lineHeight: 'var(--pf-t--global--line-height--body)',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                  color: 'var(--pf-t--global--text--color--subtle)',
                                                flex: 'none',
                                                order: 1,
                                                alignSelf: 'stretch',
                                                flexGrow: 0
                                              }}>
                                                {getExportFormatDescription(exp.format.toLowerCase())}
                                              </div>
                                            </div>
                                            
                                            {/* Buttons - different based on status */}
                                            <div style={{
                                              width: '100%',
                                              flex: 'none',
                                              order: 2,
                                              alignSelf: 'stretch',
                                              flexGrow: 0,
                                              display: 'flex',
                                              gap: 'var(--pf-t--global--spacer--sm)',
                                              alignItems: 'center'
                                            }}>
                                              {/* Complete status - show Download and Actions */}
                                              {exp.status === 'complete' && (
                                                <>
                                                  <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      console.log(`Download export: ${exp.id}, format: ${exp.format}, format lower: ${exp.format.toLowerCase()}`);
                                                      const formatLower = exp.format.toLowerCase();
                                                      if (formatLower === 'vmdk') {
                                                        setShowDownloadToast(true);
                                                      }
                                                    }}
                                                  >
                                                    Download
                                                  </Button>
                                                  <Dropdown
                                                    isOpen={openActionsDropdown[`${build.id}-${exp.id}`] || false}
                                                    onOpenChange={(isOpen) => {
                                                      setOpenActionsDropdown(prev => ({
                                                        ...prev,
                                                        [`${build.id}-${exp.id}`]: isOpen
                                                      }));
                                                    }}
                                                    onSelect={() => {
                                                      setOpenActionsDropdown(prev => ({
                                                        ...prev,
                                                        [`${build.id}-${exp.id}`]: false
                                                      }));
                                                    }}
                                                    toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                                      <Button
                                                        ref={toggleRef as React.Ref<HTMLButtonElement>}
                                                        variant="secondary"
                                                        size="sm"
                                                        aria-expanded={openActionsDropdown[`${build.id}-${exp.id}`] || false}
                                                        aria-label={`Actions for ${exp.format}`}
                                                        aria-haspopup="menu"
                                                        icon={<CaretDownIcon />}
                                                        iconPosition="right"
                                                        onClick={() => {
                                                          setOpenActionsDropdown(prev => ({
                                                            ...prev,
                                                            [`${build.id}-${exp.id}`]: !(openActionsDropdown[`${build.id}-${exp.id}`] || false)
                                                          }));
                                                        }}
                                                      >
                                                        Actions
                                                      </Button>
                                                    )}
                                                  >
                                                    <DropdownList>
                                                      <DropdownItem
                                                        onClick={() => navigateToImageBuildDetails(build.id, 'logs')}
                                                      >
                                                        View logs
                                                      </DropdownItem>
                                                      <DropdownItem
                                                        onClick={() => {
                                                          console.log(`Delete export: ${exp.id}`);
                                                          // TODO: Implement delete export logic
                                                        }}
                                                      >
                                                        Delete
                                                      </DropdownItem>
                                                      <DropdownItem
                                                        onClick={() => {
                                                          setOpenActionsDropdown(prev => ({
                                                            ...prev,
                                                            [`${build.id}-${exp.id}`]: false
                                                          }));
                                                          setBuildToRebuild(build);
                                                          setRebuildSource('card');
                                                          setShowRebuildModal(true);
                                                        }}
                                                      >
                                                        Rebuild
                                                      </DropdownItem>
                                                    </DropdownList>
                                                  </Dropdown>
                                                </>
                                              )}
                                              {/* Failed export on complete build: only last failed ISO gets Retry; all get Action (View logs, Delete) */}
                                              {exp.status === 'failed' && build.status === 'complete' && (
                                                <>
                                                  {exp.id === lastFailedIsoId && (
                                                    <Button
                                                      variant="primary"
                                                      size="sm"
                                                      icon={<RedoIcon />}
                                                      onClick={() => {
                                                        console.log(`Retry export for build: ${build.id}, format: ${exp.format}`);
                                                        // TODO: Implement retry logic
                                                      }}
                                                    >
                                                      Retry
                                                    </Button>
                                                  )}
                                                  <Dropdown
                                                    isOpen={openActionsDropdown[`${build.id}-${exp.id}`] || false}
                                                    onOpenChange={(isOpen) => {
                                                      setOpenActionsDropdown(prev => ({
                                                        ...prev,
                                                        [`${build.id}-${exp.id}`]: isOpen
                                                      }));
                                                    }}
                                                    onSelect={() => {
                                                      setOpenActionsDropdown(prev => ({
                                                        ...prev,
                                                        [`${build.id}-${exp.id}`]: false
                                                      }));
                                                    }}
                                                    toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                                      <Button
                                                        ref={toggleRef as React.Ref<HTMLButtonElement>}
                                                        variant="secondary"
                                                        size="sm"
                                                        aria-expanded={openActionsDropdown[`${build.id}-${exp.id}`] || false}
                                                        aria-label={`Actions for ${exp.format}`}
                                                        aria-haspopup="menu"
                                                        icon={<CaretDownIcon />}
                                                        iconPosition="right"
                                                        onClick={() => {
                                                          setOpenActionsDropdown(prev => ({
                                                            ...prev,
                                                            [`${build.id}-${exp.id}`]: !(openActionsDropdown[`${build.id}-${exp.id}`] || false)
                                                          }));
                                                        }}
                                                      >
                                                        Actions
                                                      </Button>
                                                    )}
                                                  >
                                                    <DropdownList>
                                                      <DropdownItem
                                                        onClick={() => navigateToImageBuildDetails(build.id, 'logs')}
                                                      >
                                                        View logs
                                                      </DropdownItem>
                                                      <DropdownItem
                                                        onClick={() => {
                                                          console.log(`Delete export: ${exp.id}`);
                                                          setOpenActionsDropdown(prev => ({
                                                            ...prev,
                                                            [`${build.id}-${exp.id}`]: false
                                                          }));
                                                          // TODO: Implement delete export logic
                                                        }}
                                                      >
                                                        Delete
                                                      </DropdownItem>
                                                    </DropdownList>
                                                  </Dropdown>
                                                </>
                                              )}
                                              {/* If build is failed, only show View logs */}
                                              {exp.status === 'failed' && build.status === 'failed' && (
                                                <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  onClick={() => navigateToImageBuildDetails(build.id, 'logs')}
                                                >
                                                  View logs
                                                </Button>
                                              )}
                                              {(exp.status === 'pending' || exp.status === 'exporting') && (
                                                <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  onClick={() => navigateToImageBuildDetails(build.id, 'logs')}
                                                >
                                                  View logs
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                          
                                          {/* Footer - Created date (empty for ISO card in last row to keep height equal) */}
                                          <div style={{
                                            width: '100%',
                                            paddingTop: 'var(--pf-t--global--spacer--md)',
                                            marginTop: 'auto',
                                            flex: 'none',
                                            order: 3,
                                            alignSelf: 'stretch',
                                            flexGrow: 0
                                          }}>
                                            <div style={{
                                              fontFamily: 'var(--pf-t--global--font--family--body)',
                                              fontStyle: 'normal',
                                              fontWeight: 'var(--pf-t--global--font--weight--body)',
                                              fontSize: 'var(--pf-t--global--font--size--sm)',
                                              lineHeight: 'var(--pf-t--global--line-height--body)',
                                              color: 'var(--pf-t--global--text--color--subtle)'
                                            }}>
                                              {build.id === 'build-6' && exp.format.toLowerCase() === 'iso'
                                                ? '\u00A0'
                                                : `Date: ${formatDateWithRelativeTime(exp.created)}`}
                                            </div>
                                          </div>
                                        </Card>
                                      </FlexItem>
                                    ))}
                                  </Flex>
                                );
                              }
                              
                              // For queued builds, show pending cards
                              if (build.status === 'queued') {
                                const pendingExports = buildExports.filter(exp => exp.status === 'pending' || exp.status === 'exporting');
                                if (pendingExports.length === 0) return null;
                                
                                const formatIconMap: Record<string, React.ReactElement> = {
                                  'vmdk': <VirtualMachineIcon />,
                                  'qcow2': <CloudSecurityIcon />,
                                  'qcow2-container': <BuilderImageIcon />,
                                  'iso': <ServerGroupIcon />
                                };
                                
                                return (
                                  <Flex gap={{ default: 'gapMd' as const }} wrap="wrap">
                                    {pendingExports.map((exp) => {
                                      return (
                                        <FlexItem key={exp.id}>
                                        <Card
                                          style={{
                                            boxSizing: 'border-box',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            padding: '16px',
                                            position: 'relative',
                                            width: '235px',
                                            height: 'auto',
                                            minHeight: '196px',
                                            background: 'var(--pf-t--global--background--color--primary--default)',
                                            border: '1px solid var(--pf-t--global--border--color--default)',
                                            borderRadius: '16px',
                                          }}
                                        >
                                          {/* Status Badge in top right */}
                                          <div 
                                            style={{
                                              position: 'absolute',
                                              right: '16px',
                                              top: '16px',
                                              display: 'flex',
                                              justifyContent: 'center',
                                              alignItems: 'center',
                                              zIndex: 1
                                            }}
                                          >
                                            {getExportStatusBadge(exp.status)}
                                          </div>
                                          
                                          {/* Card Content - Icon, Title, Description */}
                                          <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            padding: '0px',
                                            gap: '16px',
                                            width: '203px',
                                            flex: '1 1 auto',
                                            order: 0
                                          }}>
                                            {/* Icon */}
                                            <div style={{
                                              width: '40px',
                                              height: '40px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              flex: 'none',
                                              order: 0,
                                              flexGrow: 0
                                            }}>
                                              <Icon size="xl">
                                                {formatIconMap[exp.format.toLowerCase()] || <CloudSecurityIcon />}
                                              </Icon>
                                            </div>
                                            
                                            {/* Title + Description */}
                                            <div style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-start',
                                              padding: '0px',
                                              gap: '4px',
                                              width: '203px',
                                              flex: 'none',
                                              order: 1,
                                              alignSelf: 'stretch',
                                              flexGrow: 0
                                            }}>
                                              {/* Title + info icon (hover shows format filename) */}
                                              <div style={{
                                                width: '203px',
                                                height: '24px',
                                                fontFamily: 'var(--pf-t--global--font--family--heading)',
                                                fontStyle: 'normal',
                                                fontWeight: 'var(--pf-t--global--font--weight--heading--md)',
                                                fontSize: 'var(--pf-t--global--font--size--md)',
                                                lineHeight: 'var(--pf-t--global--line-height--heading--md)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--pf-t--global--spacer--xs)',
                                                color: 'var(--pf-t--global--text--color--default)',
                                                flex: 'none',
                                                order: 0,
                                                alignSelf: 'stretch',
                                                flexGrow: 0
                                              }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getExportFormatTitle(exp.format.toLowerCase())}</span>
                                                <Tooltip content={getExportFilename(build, exp.format)}>
                                                  <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon size="sm"><InfoCircleIcon /></Icon></span>
                                                </Tooltip>
                                              </div>
                                              
                                              {/* Description */}
                                              <div style={{
                                                width: '203px',
                                                fontFamily: 'var(--pf-t--global--font--family--body)',
                                                fontStyle: 'normal',
                                                  fontWeight: 'var(--pf-t--global--font--weight--body)',
                                                  fontSize: 'var(--pf-t--global--font--size--sm)',
                                                  lineHeight: 'var(--pf-t--global--line-height--body)',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                  color: 'var(--pf-t--global--text--color--subtle)',
                                                flex: 'none',
                                                order: 1,
                                                alignSelf: 'stretch',
                                                flexGrow: 0
                                              }}>
                                                {getExportFormatDescription(exp.format.toLowerCase())}
                                              </div>
                                            </div>
                                            
                                            {/* View logs button */}
                                            <div style={{
                                              width: '100%',
                                              flex: 'none',
                                              order: 2,
                                              alignSelf: 'stretch',
                                              flexGrow: 0,
                                              display: 'flex',
                                              gap: 'var(--pf-t--global--spacer--sm)',
                                              alignItems: 'center'
                                            }}>
                                              <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => {
                                                  navigateToImageBuildDetails(build.id, 'logs');
                                                }}
                                              >
                                                View logs
                                              </Button>
                                            </div>
                                          </div>
                                          
                                          {/* Footer - Created date */}
                                          <div style={{
                                            width: '100%',
                                            paddingTop: 'var(--pf-t--global--spacer--md)',
                                            marginTop: 'auto',
                                            flex: 'none',
                                            order: 3,
                                            alignSelf: 'stretch',
                                            flexGrow: 0
                                          }}>
                                            <div style={{
                                              fontFamily: 'var(--pf-t--global--font--family--body)',
                                              fontStyle: 'normal',
                                                    fontWeight: 'var(--pf-t--global--font--weight--body)',
                                                    fontSize: 'var(--pf-t--global--font--size--sm)',
                                                    lineHeight: 'var(--pf-t--global--line-height--body)',
                                                    color: 'var(--pf-t--global--text--color--subtle)'
                                            }}>
                                              Date: {formatDateWithRelativeTime(exp.created)}
                                            </div>
                                          </div>
                                        </Card>
                                      </FlexItem>
                                      );
                                    })}
                                  </Flex>
                                );
                              }
                              
                              // For building and pushing builds, show 3 cards: all without status, all with "Export image" buttons
                              if (build.status === 'building' || build.status === 'pushing') {
                                // Get all exports for this build
                                let allExports = buildExports.filter(exp => exp.buildId === build.id);
                                
                                // Ensure we have exactly 4 cards with all four formats (iso, qcow2-container, vmdk, qcow2)
                                const requiredFormats = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                                const existingFormats = allExports.map(exp => exp.format.toLowerCase());
                                
                                // Create missing export formats if needed
                                requiredFormats.forEach((format, index) => {
                                  if (!existingFormats.includes(format)) {
                                    const formatUpper = format.toUpperCase() as 'VMDK' | 'QCOW2' | 'ISO';
                                    allExports.push({
                                      id: `export-${build.id}-${format}-${index}`,
                                      buildId: build.id,
                                      buildName: build.buildName || build.name,
                                      format: formatUpper,
                                      status: 'exporting' as const,
                                      created: build.created,
                                    });
                                  }
                                });
                                
                                // Sort to ensure consistent order: vmdk, qcow2, iso
                                const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                                allExports.sort((a, b) => {
                                  const aIndex = formatOrder.indexOf(a.format.toLowerCase());
                                  const bIndex = formatOrder.indexOf(b.format.toLowerCase());
                                  return aIndex - bIndex;
                                });
                                
                                // Take exactly 4 exports
                                const exportsToShow = allExports.slice(0, 4);
                                
                                const formatIconMap: Record<string, React.ReactElement> = {
                                  'vmdk': <VirtualMachineIcon />,
                                  'qcow2': <CloudSecurityIcon />,
                                  'qcow2-container': <BuilderImageIcon />,
                                  'iso': <ServerGroupIcon />
                                };
                                
                                return (
                                  <Flex gap={{ default: 'gapMd' as const }} wrap="wrap">
                                    {exportsToShow.map((exp) => (
                                      <FlexItem key={exp.id}>
                                        <Card
                                          style={{
                                            boxSizing: 'border-box',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            padding: '16px',
                                            position: 'relative',
                                            width: '235px',
                                            height: 'auto',
                                            minHeight: '196px',
                                            background: 'var(--pf-t--global--background--color--primary--default)',
                                            border: '1px solid var(--pf-t--global--border--color--default)',
                                            borderRadius: '16px',
                                          }}
                                        >
                                          {/* Status Badge in top right */}
                                          <div 
                                            style={{
                                              position: 'absolute',
                                              right: '16px',
                                              top: '16px',
                                              display: 'flex',
                                              justifyContent: 'center',
                                              alignItems: 'center',
                                              zIndex: 1
                                            }}
                                          >
                                            {getExportStatusBadge(exp.status)}
                                          </div>
                                          
                                          {/* Card Content - Icon, Title, Description */}
                                          <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            padding: '0px',
                                            gap: '16px',
                                            width: '203px',
                                            flex: '1 1 auto',
                                            order: 0
                                          }}>
                                            {/* Icon */}
                                            <div style={{
                                              width: '40px',
                                              height: '40px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              flex: 'none',
                                              order: 0,
                                              flexGrow: 0
                                            }}>
                                              <Icon size="xl">
                                                {formatIconMap[exp.format.toLowerCase()] || <CloudSecurityIcon />}
                                              </Icon>
                                            </div>
                                            
                                            {/* Title + Description */}
                                            <div style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-start',
                                              padding: '0px',
                                              gap: '4px',
                                              width: '203px',
                                              flex: 'none',
                                              order: 1,
                                              alignSelf: 'stretch',
                                              flexGrow: 0
                                            }}>
                                              {/* Title + info icon (hover shows format filename) */}
                                              <div style={{
                                                width: '203px',
                                                height: '24px',
                                                fontFamily: 'var(--pf-t--global--font--family--heading)',
                                                fontStyle: 'normal',
                                                fontWeight: 'var(--pf-t--global--font--weight--heading--md)',
                                                fontSize: 'var(--pf-t--global--font--size--md)',
                                                lineHeight: 'var(--pf-t--global--line-height--heading--md)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--pf-t--global--spacer--xs)',
                                                color: 'var(--pf-t--global--text--color--default)',
                                                flex: 'none',
                                                order: 0,
                                                alignSelf: 'stretch',
                                                flexGrow: 0
                                              }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getExportFormatTitle(exp.format.toLowerCase())}</span>
                                                <Tooltip content={getExportFilename(build, exp.format)}>
                                                  <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon size="sm"><InfoCircleIcon /></Icon></span>
                                                </Tooltip>
                                              </div>
                                              
                                              {/* Description */}
                                              <div style={{
                                                width: '203px',
                                                fontFamily: 'var(--pf-t--global--font--family--body)',
                                                fontStyle: 'normal',
                                                  fontWeight: 'var(--pf-t--global--font--weight--body)',
                                                  fontSize: 'var(--pf-t--global--font--size--sm)',
                                                  lineHeight: 'var(--pf-t--global--line-height--body)',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                  color: 'var(--pf-t--global--text--color--subtle)',
                                                flex: 'none',
                                                order: 1,
                                                alignSelf: 'stretch',
                                                flexGrow: 0
                                              }}>
                                                {getExportFormatDescription(exp.format.toLowerCase())}
                                              </div>
                                            </div>
                                            
                                            {/* View logs button */}
                                            <div style={{
                                              width: '100%',
                                              flex: 'none',
                                              order: 2,
                                              alignSelf: 'stretch',
                                              flexGrow: 0,
                                              display: 'flex',
                                              gap: 'var(--pf-t--global--spacer--sm)',
                                              alignItems: 'center'
                                            }}>
                                              <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => navigateToImageBuildDetails(build.id, 'logs')}
                                              >
                                                View logs
                                              </Button>
                                            </div>
                                          </div>
                                          
                                          {/* Footer - Created date */}
                                          <div style={{
                                            width: '100%',
                                            paddingTop: 'var(--pf-t--global--spacer--md)',
                                            marginTop: 'auto',
                                            flex: 'none',
                                            order: 3,
                                            alignSelf: 'stretch',
                                            flexGrow: 0
                                          }}>
                                            <div style={{
                                              fontFamily: 'var(--pf-t--global--font--family--body)',
                                              fontStyle: 'normal',
                                                    fontWeight: 'var(--pf-t--global--font--weight--body)',
                                                    fontSize: 'var(--pf-t--global--font--size--sm)',
                                                    lineHeight: 'var(--pf-t--global--line-height--body)',
                                                    color: 'var(--pf-t--global--text--color--subtle)'
                                            }}>
                                              Date: {formatDateWithRelativeTime(exp.created)}
                                            </div>
                                          </div>
                                        </Card>
                                      </FlexItem>
                                    ))}
                                  </Flex>
                                );
                              }
                              
                              return null;
                            })()}
                          </StackItem>

                              {/* Export images table - Remove for queued/building/pushing, keep only for other statuses */}
                              {build.status !== 'failed' && build.status !== 'complete' && build.status !== 'queued' && build.status !== 'building' && build.status !== 'pushing' && (
                                <StackItem>
                                  <Stack hasGutter>
                                    <StackItem>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--pf-t--global--spacer--sm)' }}>
                                        <span style={{ fontWeight: 'var(--pf-t--global--font--weight--heading--md)' }}>Export images</span>
                                      </div>
                                    </StackItem>
                                    <StackItem>
                                  {(() => {
                                    const buildExports = imageExports.filter(exp => exp.buildId === build.id);
                                    
                                    // For non-failed builds, show the table only if there are exports
                                    if (buildExports.length === 0) {
                                      return null;
                                    }
                                  
                                  return (
                                    <div className="app-data-table app-data-table--visible-overflow" style={{ position: 'relative' }}>
                                      {/* Table Header */}
                                      <div className="app-data-table__header app-data-table__grid app-data-table__grid--image-export-nested">
                                        <div>Name</div>
                                        <div>Format</div>
                                        <div>Status</div>
                                        <div>Created</div>
                                        <div></div>
                                      </div>
                                      
                                      {/* Table Rows */}
                                      {buildExports.map((exp, index) => (
                                        <div
                                          key={exp.id}
                                          className="app-data-table__row app-data-table__grid app-data-table__grid--image-export-nested"
                                          style={{
                                            borderBottom: index < buildExports.length - 1
                                              ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)' 
                                              : 'none',
                                          }}
                                        >
                                          <div data-label="Name">
                                            <TruncatedLabel content={build.buildName || build.name} />
                                          </div>
                                          <div data-label="Format" style={{ display: 'flex', alignItems: 'center' }}>
                                            <StatusLabel status="info">
                                              {getExportFormatLabel(exp.format.toLowerCase())}
                                            </StatusLabel>
                                          </div>
                                          <div data-label="Status" style={{ display: 'flex', alignItems: 'center' }}>
                                            {getExportStatusBadge(exp.status)}
                                          </div>
                                          <div data-label="Created">
                                            <TruncatedLabel content={formatDateWithRelativeTime(exp.created)} />
                                          </div>
                                          <div className="app-data-table__control app-data-table__control--actions">
                                            <Dropdown
                                              isOpen={openExportDropdowns[exp.id] || false}
                                              onSelect={() => {
                                                setOpenExportDropdowns(prev => ({
                                                  ...prev,
                                                  [exp.id]: false
                                                }));
                                              }}
                                              onOpenChange={(isOpen: boolean) => {
                                                setOpenExportDropdowns(prev => ({
                                                  ...prev,
                                                  [exp.id]: isOpen
                                                }));
                                              }}
                                              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                                <Button
                                                  ref={toggleRef as React.Ref<HTMLButtonElement>}
                                                  variant="plain"
                                                  size="sm"
                                                  onClick={() => {
                                                    setOpenExportDropdowns(prev => ({
                                                      ...prev,
                                                      [exp.id]: !prev[exp.id]
                                                    }));
                                                  }}
                                                  aria-expanded={openExportDropdowns[exp.id] || false}
                                                  aria-label={`Actions for ${build.name} - ${exp.format}`}
                                                  aria-haspopup="menu"
                                                  icon={<EllipsisVIcon />}
                                                />
                                              )}
                                              popperProps={{
                                                appendTo: () => document.body,
                                                enableFlip: true,
                                                position: 'end',
                                              }}
                                            >
                                              <DropdownList>
                                                {exp.status === 'failed' ? (
                                                  <>
                                                    <DropdownItem
                                                      key="retry"
                                                      onClick={() => {
                                                        setOpenExportDropdowns(prev => ({
                                                          ...prev,
                                                          [exp.id]: false
                                                        }));
                                                        console.log(`Retry export: ${exp.id}`);
                                                        // TODO: Implement retry logic
                                                      }}
                                                    >
                                                      Retry
                                                    </DropdownItem>
                                                    <DropdownItem
                                                      key="view-logs-failed"
                                                      onClick={() => {
                                                        setOpenExportDropdowns(prev => ({
                                                          ...prev,
                                                          [exp.id]: false
                                                        }));
                                                        navigateToImageBuildDetails(build.id, 'logs');
                                                      }}
                                                    >
                                                      View logs
                                                    </DropdownItem>
                                                  </>
                                                ) : exp.status === 'complete' ? (
                                                  <>
                                                    <DropdownItem
                                                      key="export"
                                                      onClick={() => {
                                                        setOpenExportDropdowns(prev => ({
                                                          ...prev,
                                                          [exp.id]: false
                                                        }));
                                                        console.log(`Export artifact: ${exp.id}`);
                                                        // TODO: Implement export/download logic
                                                      }}
                                                    >
                                                      Download
                                                    </DropdownItem>
                                                    <DropdownItem
                                                      key="view-logs-complete"
                                                      onClick={() => {
                                                        setOpenExportDropdowns(prev => ({
                                                          ...prev,
                                                          [exp.id]: false
                                                        }));
                                                        navigateToImageBuildDetails(build.id, 'logs');
                                                      }}
                                                    >
                                                      View logs
                                                    </DropdownItem>
                                                  </>
                                                ) : (
                                                  <>
                                                    <DropdownItem
                                                      key="view-logs-pending"
                                                      onClick={() => {
                                                        setOpenExportDropdowns(prev => ({
                                                          ...prev,
                                                          [exp.id]: false
                                                        }));
                                                        navigateToImageBuildDetails(build.id, 'logs');
                                                      }}
                                                    >
                                                      View logs
                                                    </DropdownItem>
                                                  </>
                                                )}
                                              </DropdownList>
                                            </Dropdown>
                                          </div>
                                        </div>
                                      ))}
                                        </div>
                                      );
                                    })()}
                                  </StackItem>
                                </Stack>
                              </StackItem>
                              )}
                            </Stack>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
                </div>
              </StackItem>

              <StackItem>
                <div style={{ paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
                  <Pagination
                    itemCount={filteredBuilds.length}
                    perPage={perPage}
                    page={page}
                    onSetPage={onSetPage}
                    widgetId="image-builds-pagination"
                    onPerPageSelect={onPerPageSelect}
                    isCompact
                  />
                </div>
              </StackItem>
            </>
          )}
        </Stack>
  );
};

// Export Tasks Tab Content Component
const ExportTasksTabContent: React.FunctionComponent<{
  imageExports: ImageExport[];
  exportPage: number;
  exportPerPage: number;
  setExportPage: (page: number) => void;
  setExportPerPage: (perPage: number) => void;
  formatDate: (dateString: string) => string;
  getExportStatusBadge: (status: ImageExport['status']) => React.ReactElement;
  navigate: (path: string) => void;
  navigateToImageBuildDetails: (buildId: string, tab?: string) => void;
}> = ({
  imageExports,
  exportPage,
  exportPerPage,
  setExportPage,
  setExportPerPage,
  formatDate,
  getExportStatusBadge,
  navigate,
  navigateToImageBuildDetails,
}) => {
  const [expandedExportRows, setExpandedExportRows] = React.useState<Set<string>>(new Set());
  const [openExportDropdowns, setOpenExportDropdowns] = React.useState<Record<string, boolean>>({});
  
  // Pagination logic
  const startIndex = (exportPage - 1) * exportPerPage;
  const endIndex = startIndex + exportPerPage;
  const paginatedExports = imageExports.slice(startIndex, endIndex);
  
  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => {
    setExportPage(newPage);
  };
  
  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setExportPerPage(newPerPage);
    setExportPage(1);
  };
  
  const toggleExportRowExpansion = (exportId: string) => {
    setExpandedExportRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(exportId)) {
        newSet.delete(exportId);
      } else {
        newSet.add(exportId);
      }
      return newSet;
    });
  };
  
  const isExportRowExpanded = (exportId: string) => expandedExportRows.has(exportId);
  
  const onExportDropdownToggle = (exportId: string) => {
    setOpenExportDropdowns(prev => ({
      ...prev,
      [exportId]: !prev[exportId]
    }));
  };

  const onExportDropdownSelect = (exportId: string) => {
    setOpenExportDropdowns(prev => ({
      ...prev,
      [exportId]: false
    }));
  };
  
  return (
    <Stack hasGutter>
      {imageExports.length === 0 ? (
        <StackItem isFilled>
          <EmptyState variant="lg">
            <Icon size="xl">
              <UploadIcon />
            </Icon>
            <Title headingLevel="h4" size="lg">
              There are no export tasks.
            </Title>
            <EmptyStateBody>
              Export tasks are automatically created when image builds complete successfully. Each selected export format (Virtualization (VMDK), Openstack/KVM (QCOW2), Bare metal installer (ISO)) will generate a separate export task.
            </EmptyStateBody>
          </EmptyState>
        </StackItem>
      ) : (
        <>
          <StackItem isFilled>
            {/* Export Tasks Table */}
            <div className="app-data-table app-data-table--visible-overflow" style={{ position: 'relative', paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
              {/* Table Header */}
              <div className="app-data-table__header app-data-table__grid app-data-table__grid--image-exports">
                <div></div>
                <div>Build name</div>
                <div>Format</div>
                <div>Status</div>
                <div>Created</div>
                <div></div>
              </div>
              
              {/* Table Rows */}
              {paginatedExports.map((exportTask, index) => {
                const isExpanded = isExportRowExpanded(exportTask.id);
                return (
                  <React.Fragment key={exportTask.id}>
                    <div
                      className="app-data-table__row app-data-table__grid app-data-table__grid--image-exports"
                      style={{
                        borderBottom: isExpanded || index < paginatedExports.length - 1
                          ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)' 
                          : 'none',
                        backgroundColor: isExpanded ? 'var(--pf-t--global--color--nonstatus--gray--50)' : 'transparent',
                      }}
                    >
                      <div className="app-data-table__control app-data-table__control--expand">
                        <Button
                          variant="plain"
                          onClick={() => toggleExportRowExpansion(exportTask.id)}
                          aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                          aria-expanded={isExpanded}
                          style={{ padding: '4px' }}
                        >
                          {isExpanded ? <AngleDownIcon /> : <AngleRightIcon />}
                        </Button>
                      </div>
                      <div data-label="Build name">
                        <Button
                          variant="link"
                          onClick={() => navigateToImageBuildDetails(exportTask.buildId)}
                          style={{
                            padding: 0,
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                          }}
                        >
                          <TruncatedLabel content={exportTask.buildName} />
                        </Button>
                      </div>
                      <div data-label="Format">
                        <StatusLabel status="info">
                          {getExportFormatLabel(exportTask.format.toLowerCase())}
                        </StatusLabel>
                      </div>
                      <div data-label="Status">
                        {getExportStatusBadge(exportTask.status)}
                      </div>
                      <div data-label="Created">
                        <TruncatedLabel content={formatDateWithRelativeTime(exportTask.created)} />
                      </div>
                      <div className="app-data-table__control app-data-table__control--actions">
                        <Dropdown
                          isOpen={openExportDropdowns[exportTask.id] || false}
                          onSelect={() => onExportDropdownSelect(exportTask.id)}
                          onOpenChange={(isOpen: boolean) => {
                            setOpenExportDropdowns(prev => ({
                              ...prev,
                              [exportTask.id]: isOpen
                            }));
                          }}
                          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                            <Button
                              ref={toggleRef as React.Ref<HTMLButtonElement>}
                              variant="plain"
                              size="sm"
                              onClick={() => onExportDropdownToggle(exportTask.id)}
                              aria-expanded={openExportDropdowns[exportTask.id] || false}
                              aria-label={`Actions for ${exportTask.buildName} - ${exportTask.format}`}
                              aria-haspopup="menu"
                              icon={<EllipsisVIcon />}
                            />
                          )}
                          popperProps={{
                            appendTo: () => document.body,
                            enableFlip: true,
                            position: 'end',
                            minWidth: '150px',
                            maxWidth: '200px',
                          }}
                        >
                          <DropdownList>
                            <DropdownItem
                              key="export-image"
                              onClick={() => {
                                onExportDropdownSelect(exportTask.id);
                                // Handle export image action
                                console.log(`Export image: ${exportTask.id}`);
                              }}
                            >
                              Export image
                            </DropdownItem>
                            <DropdownItem
                              key="view"
                              onClick={() => {
                                onExportDropdownSelect(exportTask.id);
                                navigateToImageBuildDetails(exportTask.buildId);
                              }}
                            >
                              View
                            </DropdownItem>
                          </DropdownList>
                        </Dropdown>
                      </div>
                    </div>
                    
                    {/* Expandable Row Content */}
                    {isExpanded && (
                      <div
                        style={{
                          gridColumn: '1 / -1',
                          padding: 'var(--pf-t--global--spacer--lg)',
                          backgroundColor: 'var(--pf-t--global--background--color--primary--default)',
                          borderBottom: index < paginatedExports.length - 1
                            ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)'
                            : 'none',
                        }}
                      >
                        <Stack hasGutter>
                          {exportTask.status === 'failed' && exportTask.errorMessage ? (
                            <StackItem>
                              <Alert
                                variant="danger"
                                title="Export failed"
                                isInline
                                style={{ width: '100%' }}
                              >
                                <Stack hasGutter>
                                  <StackItem>
                                    {exportTask.errorMessage}
                                  </StackItem>
                                  <StackItem>
                                    <Button
                                      variant="secondary"
                                      onClick={() => {
                                        // Handle retry logic for this specific export
                                        console.log(`Retrying export: ${exportTask.id}`);
                                        // In a real application, this would trigger a retry API call for this specific export
                                      }}
                                    >
                                      Try again
                                    </Button>
                                  </StackItem>
                                </Stack>
                              </Alert>
                            </StackItem>
                          ) : null}
                          
                          {exportTask.logs && exportTask.logs.length > 0 ? (
                            <StackItem>
                              <Title headingLevel="h4" size="md" style={{ marginBottom: 'var(--pf-t--global--spacer--sm)' }}>
                                Logs
                              </Title>
                              <div style={{
                                backgroundColor: 'var(--pf-t--global--color--nonstatus--gray--50)',
                                border: '1px solid var(--pf-t--global--color--nonstatus--gray--200)',
                                borderRadius: '16px',
                                padding: 'var(--pf-t--global--spacer--md)',
                                fontFamily: 'monospace',
                                fontSize: 'var(--pf-t--global--font--size--sm)',
                                maxHeight: '300px',
                                overflowY: 'auto',
                              }}>
                                {exportTask.logs.map((log, idx) => (
                                  <div key={idx} style={{ marginBottom: 'var(--pf-t--global--spacer--xs)' }}>
                                    {log}
                                  </div>
                                ))}
                              </div>
                            </StackItem>
                          ) : null}
                        </Stack>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </StackItem>
          
          <StackItem>
            <div style={{ paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
              <Pagination
                itemCount={imageExports.length}
                perPage={exportPerPage}
                page={exportPage}
                onSetPage={onSetPage}
                widgetId="export-tasks-pagination"
                onPerPageSelect={onPerPageSelect}
                isCompact
              />
            </div>
          </StackItem>
        </>
      )}
    </Stack>
  );
};

export { ImageBuilds };

